import { isItemVideo } from './utils/carouselHelpers';
import safePlay from './utils/safePlay';

/**
 * Custom hook for Swiper event handlers
 */
export const useSwiperHandlers = ({
    items,
    isMuted,
    isPlaying,
    pausedIntentRef,
    setCurrentIndex,
    clearAdvanceTimer
}) => {
    const handleSlideChange = (s) => {
        setCurrentIndex(s.realIndex);
        const idx = s.realIndex;
        const item = items[idx];
        const slideIsVideoLocal = isItemVideo(item);
        const swiper = s;
        
        // clear any paused-advance timer when slide changes
        clearAdvanceTimer();
        
        // Clear paused intent when transitioning to a new slide
        // This allows video to auto-play again when carousel loops back
        if (slideIsVideoLocal && pausedIntentRef.current[idx]) {
            // Only clear if this is a new visit to the slide (carousel looped)
            // We can detect this by checking if all other indices have been visited
            pausedIntentRef.current[idx] = false;
        }

        // if slide is video, stop swiper autoplay so we wait for video end
        if (slideIsVideoLocal) {
            if (swiper.autoplay) {
                swiper.autoplay.stop?.();
            }
            // Try to play the video in the active slide immediately using requestAnimationFrame
            try {
                // Retry finding the active video element across several animation frames
                let attempts = 0;
                const maxAttempts = 8;
                const tryFindAndPlay = () => {
                    attempts += 1;
                    try {
                        const activeVideo = document.querySelector('.hero-carousel .swiper-slide-active video.carousel-media');
                        if (activeVideo) {
                            if ('muted' in activeVideo) activeVideo.muted = isMuted;
                            const userPaused = !!pausedIntentRef.current[idx];
                            if (!userPaused) {
                                safePlay(activeVideo).catch(() => {});
                            }
                            return;
                        }
                        // not found, schedule another rAF until maxAttempts
                        if (attempts < maxAttempts) {
                            requestAnimationFrame(tryFindAndPlay);
                        }
                    } catch (e) { }
                };
                requestAnimationFrame(tryFindAndPlay);
            } catch (e) { }
        } else {
            // non-video: if carousel is playing, ensure autoplay runs using slide's duration
            if (isPlaying && swiper.params?.autoplay) {
                if (swiper.autoplay) {
                    swiper.autoplay.start?.();
                }
            }
        }
    };

    const handleTransitionStart = () => {
        try {
            // clear any pending advance timer while transitioning
            clearAdvanceTimer();
            const videos = document.querySelectorAll('.hero-carousel video.carousel-media');
            videos.forEach((v) => { try { v.pause(); } catch (e) { } });
        } catch (e) { /* ignore */ }
    };

    const handleTransitionEnd = (s) => {
        try {
            const idx = s.realIndex;
            const activeVideo = document.querySelector('.hero-carousel .swiper-slide-active video.carousel-media');
            if (!activeVideo) return;
            if ('muted' in activeVideo) activeVideo.muted = isMuted;
            const userPaused = !!pausedIntentRef.current[idx];
            if (!userPaused) {
                safePlay(activeVideo).catch(() => {});
            }
        } catch (e) { }
    };

    return {
        handleSlideChange,
        handleTransitionStart,
        handleTransitionEnd
    };
};
